/**
 * AdminRoleBuilder — Visual permission matrix editor for custom roles.
 * Iter 313 — Role Scoping & Permission Architecture overhaul (Phase D).
 *
 * Lets a super admin (or anyone with `users.manage`):
 *   • List all roles (system + custom)
 *   • Clone a system role into a new custom role
 *   • Edit permissions per (module × action) cell
 *   • Preview the permission grid before saving
 *   • Delete custom roles
 *
 * Backed by the existing /api/admin/rbac/roles CRUD + the new
 * /api/admin/rbac/permission-matrix metadata endpoint.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Shield, Plus, Copy, Trash2, ArrowLeft, Loader2, Save, Users, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const AdminRoleBuilder = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);     // role being edited (null if list view)
  const [creating, setCreating] = useState(false);  // new role dialog open?
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [newRolePermissions, setNewRolePermissions] = useState({});
  const [cloneSource, setCloneSource] = useState('');
  // Bulk-assign wizard state
  const [bulkRole, setBulkRole] = useState(null);
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkUserType, setBulkUserType] = useState('admin');
  const [bulkSelected, setBulkSelected] = useState({});
  const [bulkDryRun, setBulkDryRun] = useState(true);
  const [bulkResult, setBulkResult] = useState(null);

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['rbac-roles'],
    queryFn: () => api.get('/admin/rbac/roles?include_system=true').then(r => r.data),
  });

  const { data: matrix } = useQuery({
    queryKey: ['rbac-permission-matrix'],
    queryFn: () => api.get('/admin/rbac/permission-matrix').then(r => r.data),
  });

  const modules = matrix?.modules || [];
  const actions = matrix?.actions || ['view', 'create', 'edit', 'delete', 'export', 'manage'];

  const togglePerm = (perms, modKey, action) => {
    const next = { ...perms };
    const set = new Set(next[modKey] || []);
    if (set.has(action)) set.delete(action); else set.add(action);
    next[modKey] = Array.from(set);
    if (next[modKey].length === 0) delete next[modKey];
    return next;
  };

  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/admin/rbac/roles', payload).then(r => r.data),
    onSuccess: () => {
      toast.success('Role created');
      qc.invalidateQueries({ queryKey: ['rbac-roles'] });
      setCreating(false);
      setNewRoleName('');
      setNewRoleDescription('');
      setNewRolePermissions({});
      setCloneSource('');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Create failed'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ roleId, payload }) => api.put(`/admin/rbac/roles/${roleId}`, payload).then(r => r.data),
    onSuccess: () => {
      toast.success('Role saved');
      qc.invalidateQueries({ queryKey: ['rbac-roles'] });
      setEditing(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (roleId) => api.delete(`/admin/rbac/roles/${roleId}`).then(r => r.data),
    onSuccess: () => {
      toast.success('Role deleted');
      qc.invalidateQueries({ queryKey: ['rbac-roles'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Delete failed'),
  });

  const startClone = (source) => {
    setCloneSource(source.name);
    setNewRoleName(`${source.name}_copy`);
    setNewRoleDescription(`Clone of ${source.name}`);
    setNewRolePermissions(structuredClone(source.permissions || {}));
    setCreating(true);
  };

  // Bulk-assign: load candidate users when wizard is open
  const { data: usersDirectory = { users: [] } } = useQuery({
    queryKey: ['rbac-users', bulkUserType, bulkSearch],
    queryFn: () => api.get('/admin/rbac/users', {
      params: { user_type: bulkUserType, q: bulkSearch || undefined, limit: 100 },
    }).then(r => r.data),
    enabled: !!bulkRole,
    keepPreviousData: true,
  });

  const bulkAssignMutation = useMutation({
    mutationFn: (payload) => api.post('/admin/rbac/roles/bulk-assign', payload).then(r => r.data),
    onSuccess: (res) => {
      setBulkResult(res);
      if (res.dry_run) {
        toast.info(`Preview: ${res.total} user(s) would receive "${res.role_name}"`);
      } else {
        toast.success(res.message);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Bulk assign failed'),
  });

  const openBulkWizard = (role) => {
    setBulkRole(role);
    setBulkSelected({});
    setBulkSearch('');
    setBulkUserType('admin');
    setBulkDryRun(true);
    setBulkResult(null);
  };
  const closeBulkWizard = () => { setBulkRole(null); setBulkResult(null); };
  const toggleBulkUser = (uid) => setBulkSelected(prev => ({ ...prev, [uid]: !prev[uid] }));
  const selectedUserIds = Object.keys(bulkSelected).filter(k => bulkSelected[k]);

  const editingPerms = editing?.permissions || {};

  const previewSummary = useMemo(() => {
    const perms = editing ? editingPerms : newRolePermissions;
    const totalActions = Object.values(perms || {}).reduce((s, arr) => s + (arr?.length || 0), 0);
    return {
      modules: Object.keys(perms || {}).length,
      actions: totalActions,
    };
  }, [editing, editingPerms, newRolePermissions]);

  if (rolesLoading) {
    return (
      <DashboardLayout title="Role Builder">
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Role Builder">
      <div className="space-y-6" data-testid="role-builder-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Shield className="h-5 w-5 text-indigo-600" /> Role Builder
              </h1>
              <p className="text-xs text-slate-500">Create custom roles with fine-grained module × action permissions.</p>
            </div>
          </div>
          <Button onClick={() => setCreating(true)} data-testid="new-role-btn">
            <Plus className="h-4 w-4 mr-1" /> New role
          </Button>
        </div>

        {/* Roles list */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">All roles ({roles.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {roles.map(r => (
                <div key={r.id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50" data-testid={`role-row-${r.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{r.name}</span>
                      {r.is_system_role && (
                        <Badge variant="outline" className="text-[10px]">System</Badge>
                      )}
                      {r.can_see_all_levels && (
                        <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">All levels</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {Object.keys(r.permissions || {}).length} modules
                      · {Object.values(r.permissions || {}).reduce((s, a) => s + (a?.length || 0), 0)} actions
                      {r.description ? ` · ${r.description}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => openBulkWizard(r)} data-testid={`assign-role-${r.id}`}>
                      <Users className="h-3.5 w-3.5 mr-1" /> Assign to users
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => startClone(r)} data-testid={`clone-role-${r.id}`}>
                      <Copy className="h-3.5 w-3.5 mr-1" /> Clone
                    </Button>
                    {!r.is_system_role && (
                      <>
                        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setEditing(r)} data-testid={`edit-role-${r.id}`}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost" size="sm" className="h-8 text-xs text-rose-600"
                          onClick={() => {
                            if (window.confirm(`Delete role "${r.name}"? This cannot be undone.`)) {
                              deleteMutation.mutate(r.id);
                            }
                          }}
                          data-testid={`delete-role-${r.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Permission grid — shared edit/create dialog */}
        <Dialog open={creating || !!editing} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="role-edit-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? `Edit "${editing.name}"` : 'New role'}</DialogTitle>
              <DialogDescription>
                Tick which actions this role can perform per module. <strong>view</strong> controls
                whether the module even appears in their sidebar. <strong>manage</strong> implicitly
                grants every action on the module.
                {cloneSource && <span className="block mt-1 text-xs">Cloned from <strong>{cloneSource}</strong>.</span>}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {!editing && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Role name</Label>
                    <Input value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} placeholder="housing_admin" data-testid="new-role-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Input value={newRoleDescription} onChange={(e) => setNewRoleDescription(e.target.value)} placeholder="Manages housing across all events" />
                  </div>
                </div>
              )}

              <div className="text-xs text-slate-500 sticky top-0 bg-white py-1 z-10">
                Preview: <strong>{previewSummary.modules}</strong> modules · <strong>{previewSummary.actions}</strong> actions granted
              </div>

              <div className="overflow-x-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-medium">Module</th>
                      {actions.map(a => <th key={a} className="p-2 font-medium capitalize text-center">{a}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map(m => {
                      const perms = editing ? editingPerms : newRolePermissions;
                      const acts = perms?.[m.key] || [];
                      return (
                        <tr key={m.key} className="border-t hover:bg-slate-50" data-testid={`perm-row-${m.key}`}>
                          <td className="p-2 font-mono text-[11px]">
                            <div className="font-medium text-slate-800">{m.label}</div>
                            <div className="text-slate-400">{m.key}</div>
                          </td>
                          {actions.map(a => (
                            <td key={a} className="p-2 text-center">
                              <Checkbox
                                checked={acts.includes(a)}
                                onCheckedChange={() => {
                                  if (editing) {
                                    setEditing({ ...editing, permissions: togglePerm(perms, m.key, a) });
                                  } else {
                                    setNewRolePermissions(togglePerm(perms, m.key, a));
                                  }
                                }}
                                data-testid={`perm-${m.key}-${a}`}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button>
              <Button
                disabled={createMutation.isPending || updateMutation.isPending}
                onClick={() => {
                  if (editing) {
                    updateMutation.mutate({
                      roleId: editing.id,
                      payload: { name: editing.name, description: editing.description, permissions: editing.permissions },
                    });
                  } else {
                    if (!newRoleName.trim()) { toast.error('Role name is required'); return; }
                    createMutation.mutate({
                      name: newRoleName.trim(),
                      description: newRoleDescription,
                      permissions: newRolePermissions,
                    });
                  }
                }}
                data-testid="save-role-btn"
              >
                {(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                {editing ? 'Save changes' : 'Create role'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk-assign wizard */}
        <Dialog open={!!bulkRole} onOpenChange={(o) => !o && closeBulkWizard()}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-assign-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-indigo-600" /> Assign "{bulkRole?.name}" to users</DialogTitle>
              <DialogDescription>
                Tick the users to grant this role. Choose <strong>Preview</strong> to see the recipient
                list before committing. Every assignment is logged in the RBAC change feed.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-xs">Search</Label>
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400 pointer-events-none" />
                    <Input
                      value={bulkSearch}
                      onChange={(e) => setBulkSearch(e.target.value)}
                      placeholder="email or name…"
                      className="pl-8"
                      data-testid="bulk-assign-search"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">User pool</Label>
                  <select
                    value={bulkUserType}
                    onChange={(e) => { setBulkUserType(e.target.value); setBulkSelected({}); }}
                    className="w-full h-10 border rounded-md px-3 text-sm"
                    data-testid="bulk-assign-pool"
                  >
                    <option value="admin">Platform admins (db.admins)</option>
                    <option value="school">School users (db.school_users)</option>
                  </select>
                </div>
              </div>

              <div className="border rounded max-h-72 overflow-y-auto">
                {(usersDirectory.users || []).length === 0 ? (
                  <div className="py-10 text-center text-xs text-slate-400">No users match.</div>
                ) : (
                  <div className="divide-y">
                    {usersDirectory.users.map(u => (
                      <label key={u.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 text-sm" data-testid={`bulk-user-${u.id}`}>
                        <Checkbox checked={!!bulkSelected[u.id]} onCheckedChange={() => toggleBulkUser(u.id)} />
                        <div className="flex-1">
                          <div className="font-medium">{u.name || u.email}</div>
                          <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                        </div>
                        {u.is_super_admin && <Badge className="bg-rose-100 text-rose-700 text-[10px] border-rose-200">Super</Badge>}
                        {u.role && <Badge variant="outline" className="text-[10px]">{u.role}</Badge>}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Checkbox checked={bulkDryRun} onCheckedChange={(v) => setBulkDryRun(!!v)} data-testid="bulk-assign-dry-run" />
                Preview only (don't actually assign)
              </div>

              {bulkResult && (
                <div className="rounded border bg-slate-50 p-3 text-xs">
                  <div className="flex items-center gap-4">
                    <span><strong>{bulkResult.total}</strong> recipient(s)</span>
                    {!bulkResult.dry_run && (
                      <>
                        <span className="text-emerald-700"><strong>{bulkResult.assigned}</strong> assigned</span>
                        {bulkResult.failed_count > 0 && <span className="text-rose-700">{bulkResult.failed_count} failed</span>}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={closeBulkWizard}>Close</Button>
              <Button
                disabled={selectedUserIds.length === 0 || bulkAssignMutation.isPending}
                onClick={() => bulkAssignMutation.mutate({
                  role_id: bulkRole?.id,
                  user_ids: selectedUserIds,
                  user_type: bulkUserType,
                  dry_run: bulkDryRun,
                })}
                data-testid="bulk-assign-confirm"
              >
                {bulkAssignMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {bulkDryRun ? `Preview ${selectedUserIds.length}` : `Assign to ${selectedUserIds.length}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminRoleBuilder;
